import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { type Appearance, useAppearance } from '@/hooks/use-appearance'
import { cn } from '@/lib/utils'

const OPTIONS: { value: Appearance; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]

export function AppearanceDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { appearance, setAppearance } = useAppearance()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Appearance</DialogTitle>
          <DialogDescription>Choose how Kilio looks on this device.</DialogDescription>
        </DialogHeader>

        <ToggleGroup
          type="single"
          value={appearance}
          onValueChange={(value) => {
            if (value === 'light' || value === 'dark' || value === 'system') {
              setAppearance(value)
            }
          }}
          spacing={3}
          className="w-full items-stretch"
          aria-label="Theme"
        >
          {OPTIONS.map((option) => {
            const selected = appearance === option.value

            return (
              <ToggleGroupItem
                key={option.value}
                value={option.value}
                className="h-auto min-w-0 flex-1 flex-col items-stretch gap-2 p-0 hover:bg-transparent data-[state=on]:bg-transparent"
              >
                <AppearancePreview scheme={option.value} selected={selected} />
                <span className="flex items-center gap-1.5 px-0.5 text-left">
                  <span
                    aria-hidden
                    className={cn(
                      'size-3 shrink-0 ring-1 ring-foreground/40',
                      selected && 'bg-foreground ring-foreground',
                    )}
                  />
                  {option.label}
                </span>
              </ToggleGroupItem>
            )
          })}
        </ToggleGroup>
      </DialogContent>
    </Dialog>
  )
}

function AppearancePreview({
  scheme,
  selected,
}: {
  scheme: Appearance
  selected: boolean
}) {
  return (
    <div
      className={cn(
        'overflow-hidden ring-1',
        selected ? 'ring-2 ring-foreground' : 'ring-foreground/15',
      )}
    >
      {scheme === 'system' ? (
        <div className="relative">
          <div className="light">
            <ThemeChrome />
          </div>
          <div
            aria-hidden
            className="dark absolute inset-0"
            style={{ clipPath: 'polygon(58% 0, 100% 0, 100% 100%, 46% 100%)' }}
          >
            <ThemeChrome />
          </div>
        </div>
      ) : (
        <div className={scheme}>
          <ThemeChrome />
        </div>
      )}
    </div>
  )
}

function ThemeChrome() {
  return (
    <div className="bg-background">
      <div className="flex h-6 items-center gap-1 border-b border-border px-1.5">
        <span className="size-2.5 bg-foreground/80" />
        <Skeleton className="h-1.5 w-8 animate-none" />
        <span className="ml-auto flex gap-1">
          <Skeleton className="size-2.5 animate-none" />
          <Skeleton className="size-2.5 animate-none" />
        </span>
      </div>
      <div className="flex h-[4.75rem]">
        <div className="flex w-[30%] flex-col gap-1 border-r border-border bg-muted/50 p-1.5">
          <Skeleton className="h-1.5 w-full animate-none" />
          <Skeleton className="h-1.5 w-3/4 animate-none" />
          <Skeleton className="h-1.5 w-5/6 animate-none" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-1.5">
          <span className="flex items-center gap-1">
            <Skeleton className="size-2 animate-none" />
            <Skeleton className="h-1.5 w-3/5 animate-none" />
          </span>
          <span className="flex items-center gap-1">
            <Skeleton className="size-2 animate-none" />
            <Skeleton className="h-1.5 w-2/5 animate-none" />
          </span>
          <span className="flex items-center gap-1">
            <Skeleton className="size-2 animate-none" />
            <Skeleton className="h-1.5 w-1/2 animate-none" />
          </span>
          <Skeleton className="mt-auto h-5 w-full animate-none" />
        </div>
      </div>
    </div>
  )
}
